// src/components/maintenance/DetalleProveedor.tsx

import React, { useState, useEffect } from 'react';
import { proveedorService } from '../../services/maintenance/proveedorService';
import {
    X,
    User,
    Mail,
    Phone,
    MapPin,
    Star,
    Calendar,
    Clock,
    Package,
    Award,
    FileText,
    Tag,
    ChevronRight,
    Search,
    Info,
    AlertCircle,
    CheckCircle,
    ArrowLeft,
    ShoppingCart
} from 'lucide-react';
import toast from 'react-hot-toast';

interface ProveedorDetalle {
    id: number;
    codigo: string;
    razon_social: string;
    ruc: string;
    contacto: string;
    telefono: string;
    email: string;
    direccion: string;
    calificacion: 'A' | 'B' | 'C';
    dias_entrega_promedio: number;
    tiempo_respuesta_promedio_horas: number;
    especializacion: string[];
    activo: boolean;
    created_at: string;
    updated_at: string;
    metricas: {
        total_pedidos: number;
        entregas_a_tiempo: number;
        porcentaje_puntualidad: number;
        calidad_promedio: number;
        total_comprado: number;
    };
}

interface Cotizacion {
    id: number;
    item: {
        id: number;
        nombre: string;
        codigo: string;
    };
    precio_unitario: number;
    stock_disponible: number;
    tiempo_entrega_dias: number;
    fecha_vigencia_hasta: string | null;
    es_preferido: boolean;
}

interface Paquete {
    id: number;
    nombre: string;
    descripcion: string;
    precio_paquete: number;
    total_items: number;
    descuento_porcentaje: number;
    activo: boolean;
}

interface PaqueteDetalle extends Paquete {
    precio_individual: number;
    ahorro: number;
    items: {
        item_catalogo_id: number;
        nombre: string;
        categoria: string;
        cantidad: number;
    }[];
}

interface Props {
    proveedorId: number;
    onClose: () => void;
}

type TabType = 'info' | 'items' | 'paquetes' | 'especializaciones';

const DetalleProveedor: React.FC<Props> = ({ proveedorId, onClose }) => {
    const [proveedor, setProveedor] = useState<ProveedorDetalle | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<TabType>('info');

    const [cotizaciones, setCotizaciones] = useState<Cotizacion[]>([]);
    const [paquetes, setPaquetes] = useState<Paquete[]>([]);
    const [loadingData, setLoadingData] = useState(false);

    const [selectedPaquete, setSelectedPaquete] = useState<PaqueteDetalle | null>(null);
    const [loadingPaquete, setLoadingPaquete] = useState(false);

    useEffect(() => {
        loadProveedor();
    }, [proveedorId]);

    useEffect(() => {
        if (activeTab === 'items') {
            loadCotizaciones();
            setSelectedPaquete(null);
        } else if (activeTab === 'paquetes') {
            loadPaquetes();
            setSelectedPaquete(null);
        } else {
            setSelectedPaquete(null);
        }
    }, [activeTab, proveedorId]);

    const loadProveedor = async () => {
        try {
            setLoading(true);
            const response = await proveedorService.getProveedorDetalle(proveedorId);
            if (response.success) {
                setProveedor(response.data);
            }
        } catch (error) {
            console.error('Error cargando proveedor:', error);
            toast.error('Error al cargar el proveedor');
        } finally {
            setLoading(false);
        }
    };

    const loadCotizaciones = async () => {
        try {
            setLoadingData(true);
            const response = await proveedorService.getCotizaciones({ proveedor_id: proveedorId });
            if (response.success) {
                setCotizaciones(response.data);
            }
        } catch (error) {
            console.error('Error cargando cotizaciones:', error);
            toast.error('Error al cargar la lista de precios');
        } finally {
            setLoadingData(false);
        }
    };

    const loadPaquetes = async () => {
        try {
            setLoadingData(true);
            const response = await proveedorService.getPaquetes(proveedorId);
            if (response.success) {
                setPaquetes(response.data);
            }
        } catch (error) {
            console.error('Error cargando paquetes:', error);
            toast.error('Error al cargar los paquetes');
        } finally {
            setLoadingData(false);
        }
    };

    const loadPaqueteDetalle = async (paqueteId: number) => {
        try {
            setLoadingPaquete(true);
            const response = await proveedorService.getPaqueteDetalle(paqueteId);
            if (response.success) {
                setSelectedPaquete(response.data);
            }
        } catch (error) {
            console.error('Error cargando detalle de paquete:', error);
            toast.error('Error al cargar el detalle del paquete');
        } finally {
            setLoadingPaquete(false);
        }
    };

    const getCalificacionColor = (calificacion: string) => {
        switch (calificacion) {
            case 'A':
                return 'bg-green-100 text-green-800 border-green-300';
            case 'B':
                return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'C':
                return 'bg-red-100 text-red-800 border-red-300';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-300';
        }
    };

    const getCalificacionStars = (calificacion: string) => {
        const stars = { A: 5, B: 4, C: 3 }[calificacion as 'A' | 'B' | 'C'] || 0;
        return Array(5)
            .fill(0)
            .map((_, i) => (
                <Star
                    key={i}
                    size={20}
                    className={i < stars ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                />
            ));
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Cargando...</p>
                </div>
            </div>
        );
    }

    if (!proveedor) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6 flex justify-between items-start">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <h2 className="text-2xl font-bold text-white">
                                {proveedor.razon_social}
                            </h2>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold border uppercase tracking-wider ${getCalificacionColor(proveedor.calificacion)}`}>
                                Calificación {proveedor.calificacion}
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-indigo-100 text-sm">
                            <span className="flex items-center gap-1.5"><Tag size={14} /> ID: {proveedor.codigo}</span>
                            <span className="flex items-center gap-1.5"><FileText size={14} /> RUC: {proveedor.ruc || 'N/A'}</span>
                            <div className="flex gap-1 h-4 items-center ml-2 border-l border-indigo-400/30 pl-4">
                                {getCalificacionStars(proveedor.calificacion)}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white/20 rounded-xl p-2.5 transition-all"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Navigation Tabs */}
                <div className="border-b bg-gray-50/50 px-8 flex overflow-x-auto no-scrollbar">
                    <nav className="flex gap-2">
                        {(['info', 'items', 'paquetes', 'especializaciones'] as TabType[]).map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`py-4 px-4 font-semibold text-sm transition-all relative whitespace-nowrap ${activeTab === tab
                                    ? 'text-indigo-600'
                                    : 'text-gray-500 hover:text-gray-800'
                                    }`}
                            >
                                <div className="flex items-center gap-2">
                                    {tab === 'info' && <Info size={18} />}
                                    {tab === 'items' && <Tag size={18} />}
                                    {tab === 'paquetes' && <Package size={18} />}
                                    {tab === 'especializaciones' && <Award size={18} />}
                                    {tab === 'info' ? 'Resumen' :
                                        tab === 'items' ? 'Lista de Precios' :
                                            tab === 'paquetes' ? 'Paquetes' : 'Especialidad'}
                                </div>
                                {activeTab === tab && (
                                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-indigo-600 rounded-t-full" />
                                )}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Scrollable Content Area */}
                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                    {activeTab === 'info' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                            {/* Información de Contacto */}
                            <div className="space-y-6">
                                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-3 flex items-center gap-2">
                                    <User size={14} /> Contacto & Ubicación
                                </h3>

                                <div className="grid grid-cols-1 gap-5">
                                    <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div className="bg-indigo-100 p-2.5 rounded-lg text-indigo-600">
                                            <User size={18} />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 font-medium uppercase mb-0.5">Responsable</p>
                                            <p className="font-semibold text-gray-900">{proveedor.contacto || 'Sin designar'}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div className="bg-indigo-100 p-2.5 rounded-lg text-indigo-600">
                                            <Phone size={18} />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 font-medium uppercase mb-0.5">Teléfono</p>
                                            <p className="font-semibold text-gray-900">{proveedor.telefono || 'Sin registrar'}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div className="bg-indigo-100 p-2.5 rounded-lg text-indigo-600">
                                            <Mail size={18} />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 font-medium uppercase mb-0.5">Correo Electrónico</p>
                                            <p className="font-semibold text-gray-900">{proveedor.email || 'Sin registrar'}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div className="bg-indigo-100 p-2.5 rounded-lg text-indigo-600">
                                            <MapPin size={18} />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 font-medium uppercase mb-0.5">Dirección Principal</p>
                                            <p className="font-semibold text-gray-900 leading-relaxed">{proveedor.direccion || 'Sin registrar'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Información Operativa */}
                            <div className="space-y-6">
                                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-3 flex items-center gap-2">
                                    <Clock size={14} /> Datos Operativos
                                </h3>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-white border border-gray-100 p-4 rounded-xl shadow-sm">
                                        <p className="text-[10px] text-gray-400 font-bold uppercase mb-2 tracking-wider">Entrega Promedio</p>
                                        <div className="flex items-end gap-1">
                                            <span className="text-2xl font-black text-gray-900">{proveedor.dias_entrega_promedio}</span>
                                            <span className="text-gray-500 text-xs font-semibold mb-1">días</span>
                                        </div>
                                    </div>
                                    <div className="bg-white border border-gray-100 p-4 rounded-xl shadow-sm">
                                        <p className="text-[10px] text-gray-400 font-bold uppercase mb-2 tracking-wider">Respuesta</p>
                                        <div className="flex items-end gap-1">
                                            <span className="text-2xl font-black text-gray-900">{proveedor.tiempo_respuesta_promedio_horas}</span>
                                            <span className="text-gray-500 text-xs font-semibold mb-1">horas</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4 pt-2">
                                    <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50/50">
                                        <div className="flex items-center gap-3">
                                            <Calendar className="text-gray-400" size={18} />
                                            <span className="text-sm font-medium text-gray-600">Fecha de Registro</span>
                                        </div>
                                        <span className="text-sm font-bold text-gray-900">{formatDate(proveedor.created_at)}</span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50/50">
                                        <div className="flex items-center gap-3">
                                            <Clock className="text-gray-400" size={18} />
                                            <span className="text-sm font-medium text-gray-600">Estado en Sistema</span>
                                        </div>
                                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${proveedor.activo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                            {proveedor.activo ? 'ACTIVO' : 'INACTIVO'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'items' && (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                    Productos Cotizados <span className="bg-gray-100 text-gray-500 px-2.5 py-0.5 rounded-full text-xs">{cotizaciones.length}</span>
                                </h3>
                                <div className="relative group">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-hover:text-indigo-500" size={16} />
                                    <input
                                        type="text"
                                        placeholder="Buscar por nombre o SKU..."
                                        className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none min-w-[280px]"
                                    />
                                </div>
                            </div>

                            {loadingData ? (
                                <div className="py-20 text-center space-y-3">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                                    <p className="text-sm text-gray-500">Actualizando lista de precios...</p>
                                </div>
                            ) : cotizaciones.length > 0 ? (
                                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-gray-50 text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                                                <th className="px-6 py-4">Ítem / Repuesto</th>
                                                <th className="px-6 py-4">SKU / Parte</th>
                                                <th className="px-6 py-4">Precio (Unit)</th>
                                                <th className="px-6 py-4">Stock</th>
                                                <th className="px-6 py-4 text-center">Entrega</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {cotizaciones.map((cot) => (
                                                <tr key={cot.id} className="hover:bg-indigo-50/30 transition-colors group">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="bg-gray-100 w-8 h-8 rounded shrink-0 flex items-center justify-center text-gray-400 group-hover:bg-indigo-100 group-hover:text-indigo-600 transition-all">
                                                                <Tag size={14} />
                                                            </div>
                                                            <span className="font-semibold text-gray-900">{cot.item.nombre}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 font-mono text-xs text-gray-500 uppercase">{cot.item.codigo}</td>
                                                    <td className="px-6 py-4">
                                                        <span className="text-sm font-bold text-gray-900">S/ {parseFloat(String(cot.precio_unitario)).toFixed(2)}</span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`text-xs font-bold ${cot.stock_disponible > 5 ? 'text-gray-600' : 'text-amber-600'}`}>
                                                            {cot.stock_disponible} und
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex justify-center">
                                                            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] font-bold">
                                                                {cot.tiempo_entrega_dias}d
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="py-16 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                                    <Search className="mx-auto text-gray-300 mb-4" size={48} />
                                    <h4 className="text-gray-900 font-bold mb-2">No se encontraron ítems</h4>
                                    <p className="text-gray-500 text-sm">Este proveedor aún no tiene cotizaciones individuales registradas.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'paquetes' && (
                        <div className="space-y-6">
                            {selectedPaquete ? (
                                <div className="animate-in fade-in slide-in-from-left-4 duration-300">
                                    <button
                                        onClick={() => setSelectedPaquete(null)}
                                        className="flex items-center gap-2 text-indigo-600 font-bold text-sm mb-6 hover:text-indigo-800 transition-colors"
                                    >
                                        <ArrowLeft size={16} /> Volver a los paquetes
                                    </button>

                                    <div className="bg-white border-2 border-indigo-100 rounded-3xl overflow-hidden shadow-sm">
                                        <div className="bg-indigo-50 p-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                                            <div className="flex items-center gap-5">
                                                <div className="bg-white p-4 rounded-2xl text-indigo-600 shadow-sm">
                                                    <Package size={32} />
                                                </div>
                                                <div>
                                                    <h3 className="text-2xl font-black text-gray-900 uppercase tracking-tight">{selectedPaquete.nombre}</h3>
                                                    <p className="text-gray-500 font-medium">{selectedPaquete.descripcion || 'Sin descripción.'}</p>
                                                </div>
                                            </div>
                                            <div className="bg-emerald-500 text-white px-5 py-2 rounded-2xl font-black text-center shadow-lg shadow-emerald-200">
                                                <p className="text-[10px] uppercase opacity-80 leading-none mb-1">Ahorro total</p>
                                                <p className="text-xl">S/ {Number(selectedPaquete.ahorro).toFixed(2)}</p>
                                            </div>
                                        </div>

                                        <div className="p-8">
                                            <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                                <ShoppingCart size={14} /> Ítems Incluidos en el Pack
                                            </h4>

                                            <div className="grid grid-cols-1 gap-3">
                                                {selectedPaquete.items.map((item, idx) => (
                                                    <div key={idx} className="flex items-center justify-between p-4 rounded-2xl bg-gray-50 hover:bg-white border border-transparent hover:border-indigo-100 hover:shadow-sm transition-all group">
                                                        <div className="flex items-center gap-4">
                                                            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-gray-400 group-hover:text-indigo-500 transition-colors">
                                                                <Tag size={18} />
                                                            </div>
                                                            <div>
                                                                <p className="font-bold text-gray-900">{item.nombre}</p>
                                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{item.categoria}</p>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <span className="bg-white px-4 py-1.5 rounded-xl border border-gray-100 text-sm font-black text-indigo-600">
                                                                x {item.cantidad}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="mt-10 pt-8 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-6">
                                                <div className="flex items-baseline gap-4">
                                                    <div className="flex flex-col">
                                                        <span className="text-[10px] text-gray-400 font-black uppercase">Precio Normal</span>
                                                        <span className="text-lg font-bold text-gray-400 line-through">S/ {Number(selectedPaquete.precio_individual).toFixed(2)}</span>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[10px] text-indigo-500 font-black uppercase">Precio Especial</span>
                                                        <span className="text-4xl font-black text-gray-900 leading-none">S/ {Number(selectedPaquete.precio_paquete).toFixed(2)}</span>
                                                    </div>
                                                </div>

                                                <button className="w-full sm:w-auto px-10 py-4 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all flex items-center justify-center gap-3">
                                                    Vincular a Rutina <ChevronRight size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                        Paquetes de Oferta <span className="bg-gray-100 text-gray-500 px-2.5 py-0.5 rounded-full text-xs">{paquetes.length}</span>
                                    </h3>

                                    {loadingData ? (
                                        <div className="py-20 text-center space-y-3">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                                            <p className="text-sm text-gray-500">Cargando paquetes...</p>
                                        </div>
                                    ) : paquetes.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-in fade-in duration-500">
                                            {paquetes.map((pkg) => (
                                                <div key={pkg.id} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:border-indigo-300 hover:shadow-md transition-all group">
                                                    <div className="flex justify-between items-start mb-4">
                                                        <div className="bg-indigo-100 p-3 rounded-2xl text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                                                            <Package size={24} />
                                                        </div>
                                                        <span className="bg-emerald-100 text-emerald-700 text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider">
                                                            {pkg.descuento_porcentaje}% OFF
                                                        </span>
                                                    </div>

                                                    <h4 className="text-lg font-black text-gray-900 mb-2 leading-tight uppercase tracking-tight">{pkg.nombre}</h4>
                                                    <p className="text-xs text-gray-500 mb-6 line-clamp-2 h-8 leading-relaxed">
                                                        {pkg.descripcion || 'Sin descripción detallada.'}
                                                    </p>

                                                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] text-gray-400 font-black uppercase">Precio Pack</span>
                                                            <div className="flex items-baseline gap-1">
                                                                <span className="text-xs font-bold text-gray-400">S/</span>
                                                                <span className="text-2xl font-black text-gray-900">{parseFloat(String(pkg.precio_paquete)).toFixed(2)}</span>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-xs font-bold text-indigo-600 mb-1">{pkg.total_items} ítems incluidos</p>
                                                            <button
                                                                onClick={() => loadPaqueteDetalle(pkg.id)}
                                                                disabled={loadingPaquete}
                                                                className="text-indigo-600 hover:text-indigo-800 font-black text-[10px] uppercase tracking-widest flex items-center gap-1 ml-auto transition-all hover:translate-x-1"
                                                            >
                                                                {loadingPaquete ? 'Cargando...' : 'Ver detalle'} <ChevronRight size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="py-16 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                                            <Package className="mx-auto text-gray-300 mb-4" size={48} />
                                            <h4 className="text-gray-900 font-bold mb-1">Sin paquetes activos</h4>
                                            <p className="text-gray-500 text-sm">No se han configurado paquetes de mantenimiento para este proveedor.</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === 'especializaciones' && (
                        <div className="space-y-8">
                            <div className="flex items-center gap-3">
                                <div className="bg-amber-100 p-4 rounded-2xl text-amber-600">
                                    <Award size={32} />
                                </div>
                                <div>
                                    <h3 className="font-black text-xl text-gray-900 tracking-tight">Especialidades Certificadas</h3>
                                    <p className="text-sm text-gray-500">Categorías y áreas donde el proveedor demuestra mayor capacidad operativa.</p>
                                </div>
                            </div>

                            {proveedor.especializacion && proveedor.especializacion.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
                                    {proveedor.especializacion.map((spec, index) => (
                                        <div
                                            key={index}
                                            className="bg-white border-2 border-gray-100 p-6 rounded-2xl flex items-center gap-4 hover:border-indigo-200 hover:bg-indigo-50/20 transition-all cursor-default group"
                                        >
                                            <div className="bg-gray-100 p-2 rounded-lg text-gray-400 group-hover:bg-indigo-100 group-hover:text-indigo-600 transition-colors">
                                                <CheckCircle size={20} />
                                            </div>
                                            <span className="font-bold text-gray-800 uppercase text-xs tracking-wider">{spec}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                                    <AlertCircle className="text-gray-300 mx-auto mb-4" size={48} />
                                    <p className="text-gray-900 font-bold">No hay especializaciones registradas</p>
                                    <p className="text-gray-400 text-sm mt-1">El perfil del proveedor aún no ha sido categorizado.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="border-t px-8 py-5 bg-gray-50/80 backdrop-blur flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-8 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all font-black text-xs uppercase tracking-widest"
                    >
                        Cerrar Perfil
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DetalleProveedor;
